import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PostAddIcon from "@mui/icons-material/PostAdd";
import { Button, CircularProgress, Stack } from "@mui/material";
import EmptyCardDataOperationsFiles from "assets/images/EmptyCardDataOperationsFiles.png";
import { ExtratableRepoIcon } from "assets/material-custom-icons";
import { DataExtractionModal } from "components/Data-extraction/DataExtractionModal";
import { BulkActionsBar } from "components/Deal-workspace/Workspace-metadata-components/BulkActionsBar";
import { EmptyCard } from "components/Empty-card/EmptyCard";
import { EMPTY_CARD_CONTENT } from "constants/empty-card";
import { useHighlightAfterClose, useModals, useNavigationHooks, usePrompt, useUrlHooks } from "hooks";
import { isEqual, isNull } from "lodash";
import {
  useDealActions,
  useDocumentTypesSelect,
  useFileRepoColumnsSelector,
  useFilesFiltersSelector,
  useUserRoleSelector
} from "store";
import { FileRepoFilters, IFile, IFileRepo, Order } from "store/types";

import { FileType, RoleName } from "../../constants";
import { defaultFileFilters } from "../../constants/file";
import { ColumnInfo } from "./File-filters/ColumnManagerPopover";
import { FilesFilters } from "./File-filters/FileFilters";
import { ClassificationPrompt } from "./Table/ClassificationPrompt";
import { CurrencyPrompt } from "./Table/CurrencyPrompt";
import { EffectiveDatePrompt } from "./Table/EffectiveDatePrompt";
import { InvestorPrompt } from "./Table/InvestorPrompt";
import { StatusPrompt } from "./Table/StatusPrompt";
import { FilesTable } from "./Table/Table";

const noticeMsg = "Before we proceed, don't forget to select a file!";

export interface ISelectedFile {
  id: number;
  type: FileType;
  name: string;
  document_type: string | null;
  investor_id: number | null;
  deal_id: number | null;
  date: string | null;
}

type OrderByKeys = keyof Pick<IFileRepo, "deal" | "document_type" | "created_at"> | "title";
const orderByKeys: Record<string, OrderByKeys> = {
  File: "title",
  Deal: "deal",
  Classification: "document_type",
  "Add Date": "created_at"
};

const allColumns: ColumnInfo[] = [
  { id: "name", header: "Name" },
  { id: "deal", header: "Deal" },
  { id: "document_type", header: "Altius Classification" },
  { id: "investor", header: "Investor" },
  { id: "currency", header: "Currency" },
  { id: "effective_date", header: "Effective Date" },
  { id: "created_at", header: "Upload Date" },
  { id: "status", header: "Status" },
  { id: "source", header: "Source" }
];

export const FileRepository: React.FC = () => {
  const role = useUserRoleSelector();
  const filesFilters = useFilesFiltersSelector();
  const defaultUserColumns = useFileRepoColumnsSelector();

  const { errorModal, noticeModal, filesUploadModal, deleteModal } = useModals();
  const openPrompt = usePrompt();
  const { queryParams } = useUrlHooks();
  const { removeParams, setParams } = useNavigationHooks();

  const initialFilters = useMemo<FileRepoFilters>(() => {
    const dataOperations = queryParams.dataOperations as string | undefined;
    if (dataOperations) {
      try {
        return JSON.parse(dataOperations) as FileRepoFilters;
      } catch {
        // fall through
      }
    }
    return filesFilters;
  }, []);

  const [filters, setFilters] = useState<FileRepoFilters>(initialFilters);
  const [selectedFiles, setSelectedFiles] = useState<ISelectedFile[]>([]);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(defaultUserColumns);
  const [showBulkActions, setShowBulkActions] = useState<boolean>(true);

  const {
    downloadFilesRepoAction,
    extractFileRepoK1Action,
    getFilesRepoAction,
    updateFileRepoColumnsAction,
    bulkUpdateFilesRepoAction,
    deleteFileAction
  } = useDealActions();

  const documentTypes = useDocumentTypesSelect();

  const updateFileFilters = useCallback((params: Partial<FileRepoFilters> | null) => {
    if (isNull(params)) {
      return setFilters(defaultFileFilters);
    }
    setFilters((prev) => ({ ...prev, ...params }));
  }, []);

  // Sync filters → URL
  useEffect(() => {
    if (isEqual(filters, defaultFileFilters)) {
      removeParams(["dataOperations"]);
    } else {
      setParams({ search: { dataOperations: JSON.stringify(filters) } });
    }
  }, [filters]);

  // Sync URL → filters when navigating between bookmarks while staying on this page
  useEffect(() => {
    const dataOperations = queryParams.dataOperations as string | undefined;
    if (!dataOperations) return;
    try {
      const fromUrl = JSON.parse(dataOperations) as FileRepoFilters;
      setFilters((prev) => (isEqual(prev, fromUrl) ? prev : fromUrl));
    } catch {
      // ignore malformed query params
    }
  }, [queryParams.dataOperations]);

  const handleColumnVisibilityChange = useCallback(
    (visibility: Record<string, boolean>) => {
      setColumnVisibility(visibility);
      updateFileRepoColumnsAction(visibility);
    },
    [updateFileRepoColumnsAction]
  );

  const handleCheckboxChange = (file: ISelectedFile) => {
    setSelectedFiles((prev) => {
      if (prev.some((curFile) => curFile.id === file.id)) {
        return prev.filter((curFile) => curFile.id !== file.id);
      } else {
        return [
          ...prev,
          {
            id: file.id,
            type: file.type,
            name: file.name,
            document_type: file.document_type,
            investor_id: file.investor_id,
            deal_id: file.deal_id,
            date: file.date
          }
        ];
      }
    });
  };

  const selectedFilesIds = useMemo(() => {
    return selectedFiles.map((file) => file.id);
  }, [selectedFiles]);

  const isOptionsBlocked = useMemo(() => {
    return selectedFiles.some((file) => file.type !== FileType.PDF);
  }, [selectedFiles]);

  const isExtractK1Blocked = useMemo(() => {
    return selectedFiles.some((file) => file.document_type !== "Tax");
  }, [selectedFiles]);

  // Show bulk actions bar when selection changes
  useEffect(() => {
    if (selectedFiles.length) {
      setShowBulkActions(true);
    }
  }, [selectedFiles.length]);

  const onFileZipDownload = useCallback(async () => {
    if (!selectedFilesIds.length) {
      noticeModal({ msg: noticeMsg });
      return;
    }
    await downloadFilesRepoAction(selectedFilesIds).catch((error) => {
      error.response.text().then((value: string) => {
        errorModal({ msg: JSON.parse(value)?.message || "Unknown Error" });
      });
    });
  }, [selectedFilesIds]);

  // const onAddValue = useCallback(async () => {
  //   if (!selectedFilesIds.length) {
  //     noticeModal({ msg: noticeMsg });
  //     return;
  //   }
  //   await createFileRepoTaggerLinkAction(selectedFilesIds);
  //   setSelectedFiles([]);
  // }, [selectedFilesIds]);

  const onExtract = useCallback(async () => {
    if (!selectedFilesIds.length) {
      noticeModal({ msg: noticeMsg });
      return;
    }
    await extractFileRepoK1Action(selectedFilesIds);
  }, [selectedFilesIds]);

  // const onCategorize = useCallback(() => {
  //   const files = selectedFiles.map((file) => ({
  //     name: file.name,
  //     id: file.id,
  //     document_type: file.document_type,
  //     investor_id: file.investor_id,
  //     date: file.date,
  //     deal_id: file.deal_id
  //   }));
  //   console.log("categorize", files);
  // }, [selectedFiles]);

  // ------- table data handlers
  const [files, setFiles] = useState<IFileRepo[]>([]);
  const [isDataLoading, setIsDataLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const [sorting, setSorting] = useState<{ order: Order | null; orderBy: string | null }>({
    order: "desc",
    orderBy: "Add Date"
  });

  const [hasMore, setHasMore] = useState<boolean>(true);

  const updateFile = useCallback((fileId: number, updates: Partial<IFileRepo>) => {
    setFiles((prev) => prev.map((f) => (f.id === fileId ? { ...f, ...updates } : f)));
  }, []);

  const removeFile = useCallback((fileId: number) => {
    setFiles((prev) => prev.filter((f) => f.id !== fileId));
  }, []);

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        setSelectedFiles(
          files.map((file) => ({
            id: file.id,
            type: file.type,
            name: file.name,
            document_type: file.document_type || null,
            investor_id: file.investor?.id ?? null,
            deal_id: file.deal?.id ?? null,
            date: file.created_at ?? null
          }))
        );
      } else {
        setSelectedFiles([]);
      }
    },
    [files]
  );

  const getTableData = useCallback(
    async ({
      page,
      filters,
      sort
    }: {
      page: number;
      filters: FileRepoFilters;
      sort: { order: Order | null; orderBy: string | null };
    }) => {
      try {
        setIsDataLoading(true);
        const {
          files: rawFiles,
          last_page,
          current_page
        } = await getFilesRepoAction({
          page,
          filters,
          ...(sort.order && sort.orderBy && { sort: { order: sort.order, orderBy: orderByKeys[sort.orderBy] } })
        });
        setFiles((prev) => [...prev, ...rawFiles]);
        if (Number(current_page) >= last_page) {
          setHasMore(false);
        } else {
          setPage(page + 1);
        }
      } catch (err) {
        console.log(err);
      } finally {
        setIsDataLoading(false);
      }
    },
    [hasMore]
  );

  const handleSort = (columnKey: string) => {
    const nextOrderSteps: Record<Order, Order | null> = {
      asc: "desc",
      desc: null
    };
    setSorting((prevState) => {
      const resultOrder = !prevState.order ? "asc" : nextOrderSteps[prevState.order];
      return { order: resultOrder, orderBy: resultOrder ? columnKey : null };
    });
  };

  const isMounted: React.MutableRefObject<boolean> = useRef<boolean>(false);

  const getNext = useCallback(() => {
    getTableData({ page, filters, sort: sorting });
  }, [page, filters]);

  const refetch = useCallback(() => {
    setPage(1);
    setFiles([]);
    setHasMore(true);
    getTableData({ page: 1, filters, sort: sorting });
  }, [filters, sorting]);

  // Shared with the table so both the bulk actions bar and the row action highlight the last viewed file.
  const { highlightedId, lastViewedIdRef, triggerHighlight } = useHighlightAfterClose();

  const openBulkExtractor = useCallback(
    async (initialShowParameters?: boolean) => {
      const pdfFiles = selectedFiles.filter((f) => f.type === FileType.PDF);
      if (!pdfFiles.length) {
        noticeModal({ msg: "Please select at least one PDF file." });
        return;
      }
      const fileIds = pdfFiles.map((f) => f.id);
      const fileNames = pdfFiles.reduce<Record<number, string>>((acc, f) => ({ ...acc, [f.id]: f.name }), {});
      lastViewedIdRef.current = pdfFiles[0].id;
      await openPrompt({
        componentType: "fullscreen",
        Component: DataExtractionModal,
        props: {
          fileIds,
          fileNames,
          source: "fileRepository" as const,
          onUpdateFile: updateFile,
          onCloseWithFileId: (currentFileId: number) => {
            lastViewedIdRef.current = currentFileId;
          },
          ...(initialShowParameters !== undefined && { initialShowParameters })
        }
      });
      triggerHighlight();
    },
    [selectedFiles, noticeModal, openPrompt, updateFile, lastViewedIdRef, triggerHighlight]
  );

  const onOpenDataExtractor = useCallback(() => openBulkExtractor(), [openBulkExtractor]);

  const handleBulkView = useCallback(() => openBulkExtractor(false), [openBulkExtractor]);

  const handleChangeClassification = useCallback(
    async (anchorEl: HTMLElement | null) => {
      if (!selectedFiles.length) {
        noticeModal({ msg: noticeMsg });
        return;
      }

      const result = await openPrompt({
        componentType: "popover",
        ...(anchorEl ? { anchorEl } : { anchorPosition: { top: window.innerHeight / 2, left: window.innerWidth / 2 } }),
        Component: ClassificationPrompt,
        props: {
          currentClassification: null
        }
      });

      if (!result) return;

      // Bulk update all selected files with the new classification
      const updatedFiles = await bulkUpdateFilesRepoAction({
        file_ids: selectedFiles.map((f) => f.id),
        document_type: result.classification
      });

      // Update local state with the updated files
      setFiles((prev) =>
        prev.map((f) => {
          const updated = updatedFiles.find((uf) => uf.id === f.id);
          return updated || f;
        })
      );
    },
    [selectedFiles, noticeModal, openPrompt, bulkUpdateFilesRepoAction]
  );

  const handleChangeCurrency = useCallback(
    async (anchorEl: HTMLElement | null) => {
      if (!selectedFiles.length) {
        noticeModal({ msg: noticeMsg });
        return;
      }

      const result = await openPrompt({
        componentType: "popover",
        ...(anchorEl ? { anchorEl } : { anchorPosition: { top: window.innerHeight / 2, left: window.innerWidth / 2 } }),
        Component: CurrencyPrompt,
        props: {
          currentCurrency: null
        }
      });

      if (!result || !result.currency) return;

      // Bulk update all selected files with the new currency
      const updatedFiles = await bulkUpdateFilesRepoAction({
        file_ids: selectedFiles.map((f) => f.id),
        currency: result.currency
      });

      // Update local state with the updated files
      setFiles((prev) =>
        prev.map((f) => {
          const updated = updatedFiles.find((uf) => uf.id === f.id);
          return updated || f;
        })
      );
    },
    [selectedFiles, noticeModal, openPrompt, bulkUpdateFilesRepoAction]
  );

  const handleChangeInvestor = useCallback(
    async (anchorEl: HTMLElement | null) => {
      if (!selectedFiles.length) {
        noticeModal({ msg: noticeMsg });
        return;
      }

      const result = await openPrompt({
        componentType: "popover",
        ...(anchorEl ? { anchorEl } : { anchorPosition: { top: window.innerHeight / 2, left: window.innerWidth / 2 } }),
        Component: InvestorPrompt,
        props: {
          currentInvestorId: null,
          currentAllInvestors: false,
          currentSplitByCommitment: false,
          dealId: null,
          showGrouped: false
        }
      });

      if (!result) return;

      // Bulk update all selected files with the new investor
      const updatedFiles = await bulkUpdateFilesRepoAction({
        file_ids: selectedFiles.map((f) => f.id),
        investor_id: result.investor?.id ?? null,
        all_investors: result.allInvestors,
        split_by_commitment: result.allInvestors ? true : result.splitByCommitment,
        investor_ids: result.investorIds,
        group_commitment: result.groupCommitment,
        entire_commitment: result.entireCommitment
      });

      // Update local state with the updated files
      setFiles((prev) =>
        prev.map((f) => {
          const updated = updatedFiles.find((uf) => uf.id === f.id);
          return updated || f;
        })
      );
    },
    [selectedFiles, noticeModal, openPrompt, bulkUpdateFilesRepoAction]
  );

  const handleChangeEffectiveDate = useCallback(
    async (anchorEl: HTMLElement | null) => {
      if (!selectedFiles.length) {
        noticeModal({ msg: noticeMsg });
        return;
      }

      const result = await openPrompt({
        componentType: "popover",
        ...(anchorEl ? { anchorEl } : { anchorPosition: { top: window.innerHeight / 2, left: window.innerWidth / 2 } }),
        Component: EffectiveDatePrompt,
        props: {
          currentDate: null
        }
      });

      if (!result) return;

      // Bulk update all selected files with the new effective date
      const updatedFiles = await bulkUpdateFilesRepoAction({
        file_ids: selectedFiles.map((f) => f.id),
        effective_date: result.effectiveDate
      });

      // Update local state with the updated files
      setFiles((prev) =>
        prev.map((f) => {
          const updated = updatedFiles.find((uf) => uf.id === f.id);
          return updated || f;
        })
      );
    },
    [selectedFiles, noticeModal, openPrompt, bulkUpdateFilesRepoAction]
  );

  const handleChangeStatus = useCallback(
    async (anchorEl: HTMLElement | null) => {
      if (!selectedFiles.length) {
        noticeModal({ msg: noticeMsg });
        return;
      }

      const result = await openPrompt({
        componentType: "popover",
        ...(anchorEl ? { anchorEl } : { anchorPosition: { top: window.innerHeight / 2, left: window.innerWidth / 2 } }),
        Component: StatusPrompt,
        props: {
          currentStatus: "new"
        }
      });

      if (!result) return;

      // Bulk update all selected files with the new status
      const updatedFiles = await bulkUpdateFilesRepoAction({
        file_ids: selectedFiles.map((f) => f.id),
        file_status: result.status
      });

      // Update local state with the updated files
      setFiles((prev) =>
        prev.map((f) => {
          const updated = updatedFiles.find((uf) => uf.id === f.id);
          return updated || f;
        })
      );
    },
    [selectedFiles, noticeModal, openPrompt, bulkUpdateFilesRepoAction]
  );

  const handleDelete = useCallback(() => {
    if (!selectedFiles.length) {
      noticeModal({ msg: noticeMsg });
      return;
    }

    const fileCount = selectedFiles.length;
    const fileNames = selectedFiles
      .slice(0, 3)
      .map((f) => f.name)
      .join(", ");
    const displayNames = fileCount > 3 ? `${fileNames}, and ${fileCount - 3} more` : fileNames;

    deleteModal({
      action: async () => {
        try {
          // Delete each file
          for (const file of selectedFiles) {
            if (file.deal_id) {
              const fileToDelete: Pick<IFile, "id" | "deal_id"> = {
                id: file.id,
                deal_id: file.deal_id
              };
              await deleteFileAction(fileToDelete as IFile);
            }
          }

          // Remove deleted files from local state
          const deletedIds = new Set(selectedFiles.map((f) => f.id));
          setFiles((prev) => prev.filter((f) => !deletedIds.has(f.id)));
          setSelectedFiles([]);
        } catch (_error) {
          errorModal({ msg: "Failed to delete files. Please try again." });
        }
      },
      element: `Delete ${fileCount} file${fileCount === 1 ? "" : "s"}`,
      message: `Are you sure you want to delete: ${displayNames}?`
    });
  }, [selectedFiles, noticeModal, errorModal, deleteFileAction, deleteModal]);

  // Convert ISelectedFile[] to IMetaDetails-like structure for BulkActionsBar
  const selectedItemsForBulkActions = useMemo(() => {
    return selectedFiles.map((file) => ({
      id: file.id,
      entity_type: "file" as const,
      type: file.type,
      name: file.name
    }));
  }, [selectedFiles]);

  const actionMenuOptions = useMemo(
    () => [
      {
        title: "Download and Extract K-1",
        icon: <FileDownloadOutlinedIcon />,
        onClick: onExtract,
        disabled: isOptionsBlocked || isExtractK1Blocked
      },
      {
        title: "Download",
        icon: <FileDownloadOutlinedIcon />,
        onClick: onFileZipDownload
      },
      {
        title: "Data Extractor",
        icon: <ExtratableRepoIcon sx={{ width: 20, height: 16, fill: "#071F35" }} />,
        onClick: onOpenDataExtractor,
        disabled: isOptionsBlocked,
        hidden: role?.name && ![RoleName.Admin, RoleName.Analyst].includes(role.name as RoleName)
      }
    ],
    [onExtract, onFileZipDownload, onOpenDataExtractor, isOptionsBlocked, isExtractK1Blocked, role?.name]
  );

  //first data loading
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    getTableData({ page: 1, filters, sort: sorting });
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      refetch();
    }
  }, [filters, sorting]);

  useEffect(() => {
    if (queryParams.refetchFileRepository) {
      refetch();
      removeParams(["refetchFileRepository"]);
    }
  }, [queryParams, removeParams, refetch]);

  const handleAddFile = useCallback(() => {
    filesUploadModal({
      onSuccessCallback: async () => {
        setParams({ search: { refetchFileRepository: true } });
      }
    });
  }, [filesUploadModal, setParams]);

  const hasActiveFilters =
    !!filters.deals?.length ||
    !!filters.investors?.length ||
    !!filters.document_types?.length ||
    !!filters.source?.length ||
    !!filters.file_types?.length ||
    filters.date !== null ||
    !!filters.show_latest;

  const isEmpty = !isDataLoading && !files.length && !hasActiveFilters;

  return (
    <Stack
      sx={{
        bgcolor: "white",
        borderRadius: "8px",
        padding: "16px 16px 8px 16px",
        overflow: "auto",
        boxShadow: "0 1px 2px 0 #1212171a",
        margin: "0 2px 2px 2px",
        flexGrow: 1
      }}
    >
      {isDataLoading && !files.length ? (
        <Stack alignItems="center" justifyContent="center" sx={{ flexGrow: 1 }}>
          <CircularProgress />
        </Stack>
      ) : isEmpty ? (
        <EmptyCard
          title={EMPTY_CARD_CONTENT.fileRepository.title}
          description={{
            text: EMPTY_CARD_CONTENT.fileRepository.description,
            externalText: EMPTY_CARD_CONTENT.fileRepository.externalDescription
          }}
          image={{
            src: EmptyCardDataOperationsFiles,
            sx: { width: 315, height: 140 }
          }}
          fitPage
          actionsElement={
            <Button variant="contained" onClick={handleAddFile} startIcon={<PostAddIcon />} sx={{ bgcolor: "#071F35" }}>
              Add File
            </Button>
          }
        />
      ) : (
        <>
          <Stack flexDirection="row" alignItems="center" justifyContent="space-between" mb="16px">
            <FilesFilters
              filters={filters}
              setFilters={updateFileFilters}
              disabled={isDataLoading}
              documentTypes={documentTypes}
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={handleColumnVisibilityChange}
              allColumns={allColumns}
              actionMenuOptions={actionMenuOptions}
            />
          </Stack>
          <FilesTable
            highlightedFileId={highlightedId}
            lastViewedIdRef={lastViewedIdRef}
            triggerHighlight={triggerHighlight}
            setFile={handleCheckboxChange}
            onSelectAll={handleSelectAll}
            filesIds={selectedFilesIds}
            files={files}
            isDataLoading={isDataLoading}
            hasMore={hasMore}
            getNext={getNext}
            refetch={refetch}
            updateFile={updateFile}
            removeFile={removeFile}
            sorting={sorting}
            handleSort={handleSort}
            isLoading={isDataLoading}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={handleColumnVisibilityChange}
          />
          {showBulkActions && (
            <BulkActionsBar
              selectedItems={selectedItemsForBulkActions}
              onClose={() => setShowBulkActions(false)}
              onView={handleBulkView}
              onExtract={onOpenDataExtractor}
              onDownload={onFileZipDownload}
              onChangeClassification={handleChangeClassification}
              onChangeCurrency={handleChangeCurrency}
              onChangeInvestor={handleChangeInvestor}
              onChangeEffectiveDate={handleChangeEffectiveDate}
              onChangeStatus={handleChangeStatus}
              onDelete={handleDelete}
            />
          )}
        </>
      )}
    </Stack>
  );
};
